import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useImpersonation } from '@center-technologies/shared-api-clients';
import { usePlatformAdmin } from 'design-system/my-lib';

/**
 * The platform-builder lens over one Studio (spec SC8). `operator` inference (org has approach
 * deployments) has no backend signal today — see IMPLEMENTATION_PLAN.md Open Questions
 * ("no existing signal answers 'does the org have any approach deployments'"). Until that lands,
 * every non-platform-admin caller infers `author`, the spec's own stated fallback for a user with
 * no org.
 */
export type StudioMode = 'platform' | 'operator' | 'author';
export type StudioModeSource = 'query' | 'preference' | 'inferred';

const STUDIO_MODES: readonly StudioMode[] = ['platform', 'operator', 'author'];
const STUDIO_MODE_PREFERENCE_KEY = 'center.studio.modePreference';

function isStudioMode(value: string | null): value is StudioMode {
  return value !== null && (STUDIO_MODES as readonly string[]).includes(value);
}

/** Wrapped like `StudioShell`'s sidebar preference: a browser with site data blocked throws on
 * access rather than returning null, and a stored preference is a convenience, never load-bearing. */
function readModePreference(): StudioMode | null {
  try {
    const stored = globalThis.localStorage?.getItem(STUDIO_MODE_PREFERENCE_KEY) ?? null;
    return isStudioMode(stored) ? stored : null;
  } catch {
    return null;
  }
}

function writeModePreference(mode: StudioMode): void {
  try {
    globalThis.localStorage?.setItem(STUDIO_MODE_PREFERENCE_KEY, mode);
  } catch {
    /* preference is a convenience — never let a storage failure break mode switching */
  }
}

function clearModePreference(): void {
  try {
    globalThis.localStorage?.removeItem(STUDIO_MODE_PREFERENCE_KEY);
  } catch {
    /* noop — see writeModePreference */
  }
}

export interface StudioModeResolution {
  mode: StudioMode;
  source: StudioModeSource;
}

/**
 * Pure resolver — query (`?as=`) beats stored preference beats the inferred default, exactly
 * spec SC8's order. Unit-testable with no router/hooks mounted. An unknown `?as=` value is
 * ignored ("`?as=` with an unknown value" edge case), falling through to preference/inferred.
 */
export function resolveStudioMode(
  queryValue: string | null,
  storedPreference: StudioMode | null,
  isPlatformAdmin: boolean
): StudioModeResolution {
  if (isStudioMode(queryValue)) {
    return { mode: queryValue, source: 'query' };
  }
  if (storedPreference) {
    return { mode: storedPreference, source: 'preference' };
  }
  return { mode: isPlatformAdmin ? 'platform' : 'author', source: 'inferred' };
}

export interface StudioModeContextValue {
  mode: StudioMode;
  source: StudioModeSource;
  /** Persists the choice and updates the live context; analytics is the caller's job (fired by
   * `StudioModePill`, the event's documented source). */
  setMode: (mode: StudioMode) => void;
}

const StudioModeContext = createContext<StudioModeContextValue | null>(null);

export const StudioModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [searchParams] = useSearchParams();
  const { isPlatformAdmin } = usePlatformAdmin();
  const { impersonationDetails } = useImpersonation();
  const organizationId = impersonationDetails?.organizationId;
  const sessionId = impersonationDetails?.sessionId;
  const prevOrgRef = useRef(organizationId);
  const prevSessionRef = useRef(sessionId);
  const queryValue = searchParams.get('as');

  const [resolution, setResolution] = useState<StudioModeResolution>(() =>
    resolveStudioMode(queryValue, readModePreference(), isPlatformAdmin)
  );

  // Re-resolve whenever the query param changes or the platform-admin check settles in
  // (it starts `false` while `usePlatformAdmin` loads, per its own fail-closed default).
  useEffect(() => {
    setResolution(resolveStudioMode(queryValue, readModePreference(), isPlatformAdmin));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryValue, isPlatformAdmin]);

  // A stored `platform` preference must not survive an impersonation switch that leaves the
  // acting identity no longer a platform admin — otherwise the pill/desk keep claiming platform
  // mode for an identity that can no longer see it (IMPLEMENTATION_PLAN.md Open Questions).
  useEffect(() => {
    const orgSwitched = prevOrgRef.current !== undefined && prevOrgRef.current !== organizationId;
    const identitySwitched = prevSessionRef.current !== sessionId;
    if ((orgSwitched || identitySwitched) && readModePreference() === 'platform' && !isPlatformAdmin) {
      clearModePreference();
      setResolution(resolveStudioMode(queryValue, null, isPlatformAdmin));
    }
    prevOrgRef.current = organizationId;
    prevSessionRef.current = sessionId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, sessionId]);

  const setMode = useCallback((next: StudioMode) => {
    writeModePreference(next);
    setResolution({ mode: next, source: 'preference' });
  }, []);

  const value = useMemo<StudioModeContextValue>(
    () => ({ mode: resolution.mode, source: resolution.source, setMode }),
    [resolution, setMode]
  );

  return <StudioModeContext.Provider value={value}>{children}</StudioModeContext.Provider>;
};

export function useStudioMode(): StudioModeContextValue {
  const ctx = useContext(StudioModeContext);
  if (!ctx) {
    throw new Error('useStudioMode must be used within a StudioModeProvider');
  }
  return ctx;
}
